import { StandardStatus } from '@zoocasa/go-search';
import { ListingDisplayPolicy } from './display-policy';
import { isSold, isExpired } from '../../types/reso';

/**
 * Value object that encapsulates how to present a listing's timestamp.
 * 
 * Determines what timestamp to show based on the listing's status
 * and regional display policies.
 */
export class TimestampPresentation {
  readonly #timestamp: Date | null;
  readonly #canDisplay: boolean;

  private constructor(
    timestamp: Date | null,
    canDisplay: boolean
  ) {
    this.#timestamp = timestamp;
    this.#canDisplay = canDisplay;
  }

  /**
   * Creates a TimestampPresentation from listing data.
   */
  static fromListing({
    standardStatus,
    soldAt,
    updatedOn,
    addedAt,
    displayPolicy,
    isAvailable,
    isSold: isSoldFlag,
  }: {
    standardStatus: StandardStatus | null;
    soldAt: Date | null;
    updatedOn: Date | null;
    addedAt: Date | null;
    displayPolicy: ListingDisplayPolicy;
    isAvailable?: boolean | null;
    isSold?: boolean | null;
  }): TimestampPresentation {
    // Check if timestamps can be displayed based on regional display policy
    if (!displayPolicy.canDisplayTimestamps) {
      return new TimestampPresentation(null, false);
    }
    
    let timestamp: Date | null = null;
    
    const statusIsSold = standardStatus ? isSold(standardStatus) : false;
    const isListingSold = statusIsSold || Boolean(isSoldFlag);
    const isListingExpired = (standardStatus ? isExpired(standardStatus) : false) || (isAvailable === false && !isListingSold);

    if (isListingSold) {
      timestamp = soldAt || updatedOn || addedAt;
    } else if (isListingExpired) {
      timestamp = updatedOn || addedAt;
    } else {
      timestamp = addedAt;
    }

    return new TimestampPresentation(timestamp, true);
  }

  /**
   * The timestamp to display, or null if it cannot be displayed.
   */
  get timestamp(): Date | null {
    return this.#timestamp;
  }

  /**
   * Whether the timestamp can be displayed.
   */
  get canDisplay(): boolean {
    return this.#canDisplay;
  }

  /**
   * Whether to show the timestamp (has value and can be displayed).
   */
  get shouldShow(): boolean {
    return this.#canDisplay && this.#timestamp !== null;
  }
}
